import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import { getQBCreateAssessmentInfo } from 'legacy-src/queries';
import { qbPath } from 'legacy-src/constants/paths';

/**
 * Get stimulus data of a question.
 * @param {Object} question             - The question to search for.
 * @param {Object} allStimulusQuestions - The list of stimulus questions.
 * @returns {Object} The stimulus id wrapped in an object.
 * */
export const getQuestionStimulus = (question, allStimulusQuestions) => {
  let stimulus = {};
  const allStimulusQuestionsObject = allStimulusQuestions || {};
  const loadedStimulusIds = Object.keys(allStimulusQuestionsObject).map((id) =>
    Number.parseInt(id, 10),
  );

  loadedStimulusIds.forEach((stimulusId) => {
    const stimulusQuestionIds = (
      allStimulusQuestionsObject[stimulusId] || []
    ).map((q) => q.item_id || q);
    if (stimulusQuestionIds.includes(question.id)) {
      stimulus.id = stimulusId;
    }
  });

  return stimulus;
};

/**
 * Check if a question is part of a quiz or not.
 * If the question is a simple one the ID will be present in `myQuiz`.
 * If the question shares a stimulus it will be part of `stimulusQuestionsInQuiz`.
 * @param {Number} questionId - The ID of a question to search for.
 * @param {Array} myQuiz - The list of questions and stimuli part of the quiz.
 * @param {Object} stimulusQuestionsInQuiz - Object map of stimulus with questions of the quiz.
 * @param {Object} allStimulusQuestions - Object map of loaded stimuli with questions.
 * @returns {Boolean} If a question is part of a quiz or not.
 * */
export const isQuestionInQuiz = (
  questionId,
  myQuiz,
  stimulusQuestionsInQuiz,
  allStimulusQuestions,
) => {
  if (myQuiz.includes(questionId)) {
    return true;
  }

  const questionStimulus = getQuestionStimulus(
    { id: questionId },
    allStimulusQuestions,
  );
  return (
    !isEmpty(questionStimulus) &&
    questionStimulus.id in stimulusQuestionsInQuiz &&
    stimulusQuestionsInQuiz[questionStimulus.id].includes(questionId)
  );
};

/**
 * Removes item from quiz based on a list of received ids.
 * @param {Array} myQuiz - The list of items in the quiz (questions and stimulus).
 * @param {Object} stimulusQuestionsInQuiz - Object map of stimulus and their questions from the quiz.
 * @param {Array} itemIds - The list of question IDs to be removed from the quiz.
 * @returns {Object} - The new value of `myQuiz` and of `stimulusQuestionsInQuiz`.
 * */
export const deleteMyQuizItems = (myQuiz, stimulusQuestionsInQuiz, itemIds) => {
  let newMyQuiz = [...myQuiz];
  let newStimulusQuestionsInQuiz = { ...stimulusQuestionsInQuiz };

  (itemIds || []).forEach((itemId) => {
    const { id: stimulusId } = getQuestionStimulus(
      { id: itemId },
      stimulusQuestionsInQuiz,
    );
    if (stimulusId) {
      const stimulusQuestionIndex =
        newStimulusQuestionsInQuiz[stimulusId].indexOf(itemId);
      newStimulusQuestionsInQuiz[stimulusId].splice(stimulusQuestionIndex, 1);

      if (newStimulusQuestionsInQuiz[stimulusId].length === 0) {
        delete newStimulusQuestionsInQuiz[stimulusId];
        const stimulusIndex = newMyQuiz.indexOf(stimulusId);
        newMyQuiz.splice(stimulusIndex, 1);
      }
    } else {
      const itemIndex = newMyQuiz.indexOf(itemId);
      newMyQuiz.splice(itemIndex, 1);
    }
  });

  return {
    newMyQuiz,
    newStimulusQuestionsInQuiz,
  };
};

/**
 * Get a list of all questions part of a quiz (including those under stimulus).
 * @param {Array} myQuiz - The list of questions and stimuli of the quiz.
 * @param {Object} stimulusQuestionsInQuiz - Object map of stimulus with questions of the quiz.
 * @returns {Array}
 * */
export const getItemIdsInQuiz = (myQuiz, stimulusQuestionsInQuiz) => {
  let itemIdsInQuiz = [];

  myQuiz.forEach((myQuizItemId) => {
    if (myQuizItemId in stimulusQuestionsInQuiz) {
      itemIdsInQuiz = itemIdsInQuiz.concat(
        stimulusQuestionsInQuiz[myQuizItemId],
      );
    } else {
      itemIdsInQuiz.push(myQuizItemId);
    }
  });

  return itemIdsInQuiz;
};

/**
 * Get the structure needed for the context from a batch of items and their stimulus map.
 * @param {Array} questions - The list of all questions from the batch.
 * @param {Object} questionToStimMapping - Object map of question with their stimulus.
 * @param {Array} stimuli - The list of all stimuli available based on questions in the batch.
 * @returns {Object}
 * */
export const getBatchItemsContextStructure = (
  questions,
  questionToStimMapping,
  stimuli,
) => {
  let myQuizIds = [];
  let stimuliIds = [];
  let stimuliQuestions = {};

  questions.forEach((question) => {
    if (question.id in questionToStimMapping) {
      const stimulusId = questionToStimMapping[question.id].id;
      if (!myQuizIds.includes(stimulusId)) {
        myQuizIds.push(stimulusId);
        stimuliIds.push(stimulusId);

        const stimulusData = stimuli.find(
          ({ stimulus_id }) => stimulus_id === stimulusId.toString(),
        );
        stimuliQuestions[stimulusId] = stimulusData.questions;
      }
    } else {
      myQuizIds.push(question.id);
    }
  });

  return {
    myQuizIds,
    stimuli: stimuliIds.map((stimulusId) => ({ id: stimulusId })),
    stimuliQuestions,
  };
};

export const getAssessmentData = async (
  subjectId,
  educationPeriod,
  assessmentId,
  apolloClient = {},
) => {
  const isQuestionsDataUsed = true;
  const responseResult = await apolloClient.query({
    query: getQBCreateAssessmentInfo,
    fetchPolicy: 'network-only',
    variables: {
      subjectId,
      assessmentId,
      educationPeriod,
      isQuestionsDataUsed,
    },
  });

  const {
    loading,
    data: { qbCreateEdit: response },
  } = responseResult;

  if (!loading) {
    return prepareAssessmentData(response);
  }
};

/**
 * Prepare assessment data to be set in context
 * @param {object} rawData
 * @returns {object} data Readable by QuizBuilder context
 * */
export const prepareAssessmentData = (rawData) => {
  const {
    questions,
    stimuli,
    itemToStimulusMap,
    title,
    id,
    isPracticeExam,
    isOwner,
    labels,
  } = rawData || {};
  let data = {
    quizId: id,
    quizTitle: title,
    questions: {},
    stimulusQuestionsInQuiz: {},
    stimulusQuestionsInQuizBak: {},
    myQuiz: [],
    myQuizBak: [],
    labels: labels.map((label) => label.labelId),
    allStimulusQuestions: {},
    isPracticeExam,
    isOwner,
  };

  const qnToStimMap = {};

  // populate all stimuli questions
  stimuli &&
    stimuli.forEach((stimulus) => {
      const { stimulus_id, questions: stimQuestions } = stimulus;
      const questionIds = stimQuestions.map((q) => q.item_id);
      data.allStimulusQuestions[Number.parseInt(stimulus_id, 10)] =
        stimQuestions.map((stimQn) => ({
          ...stimQn,
          // TODO(@hpaul): Return this field from backend
          shared_stimulus_question_ids: questionIds,
          used_detail: stimQn.used_detail
            ? JSON.parse(stimQn.used_detail)
            : null,
        }));
    });

  // create object that maps question to stimulus
  itemToStimulusMap &&
    itemToStimulusMap.forEach(({ question_id, stimulus }) => {
      qnToStimMap[question_id] = stimulus;
    });

  // populate questions, stimulusQuestionsInQuiz, myQuiz
  questions &&
    questions.forEach((question) => {
      const questionId = question.id;

      // we update the questions object
      data.questions[questionId] = {
        ...question,
        used_detail: question.used_detail
          ? JSON.parse(question.used_detail)
          : null,
      };

      // if question has a shared passage
      if (qnToStimMap[questionId]) {
        const stimId = qnToStimMap[questionId].id;

        // we create a mapping between the stimulus and the questions that belong to it in the quiz
        data.stimulusQuestionsInQuiz[stimId]
          ? data.stimulusQuestionsInQuiz[stimId].push(questionId)
          : (data.stimulusQuestionsInQuiz[stimId] = [questionId]);

        // and if the stimulus is not a part of myQuiz yet
        if (!data.myQuiz.includes(qnToStimMap[questionId].id)) {
          // we update myQuiz with the stimulus
          data.myQuiz.push(stimId);
        }
      } else if (!data.myQuiz.includes(questionId)) {
        // update myQuiz if it's a non-shared-passage question that is not in the array already
        data.myQuiz.push(questionId);
      }
    });

  data.myQuizBak = data.myQuiz.slice();
  data.stimulusQuestionsInQuizBak = { ...data.stimulusQuestionsInQuiz };
  return data;
};

export const redirectToPreviewItem = (items, router) => {
  const {
    location: { query },
    params: { subject },
  } = router;
  if (query['force-preview'] === 'true' && items.length) {
    const { item } = query;
    const foundItem = item === 'first' ? items[0] : items[items.length - 1];
    const itemId = foundItem.item_id;

    router.push({
      pathname: `/${subject}/${qbPath}/questions/preview/item/${itemId}`,
      query: omit(query, ['force-preview', 'myItem', 'item']),
    });

    return true;
  }
};

/**
 * Remove `used_detail` key of an item
 * @param {Object} item - The item to be updated.
 * @param {Number|String} quizId - The key to be removed.
 * @returns {Object} The updated item.
 * */
const removeUsedDetail = (item, quizId) => {
  const itemToUpdate = item;
  delete itemToUpdate.used_detail[quizId];

  const keysNumber = Object.keys(itemToUpdate.used_detail).length;
  if (keysNumber === 0) {
    itemToUpdate.used_before = false;
  }

  return itemToUpdate;
};

/**
 * Update myQuiz items `used_detail` property.
 * @param {Object} data - The data used for identify items in myQuiz.
 * @returns {Object} - Object with updated questions and stimulusQuestions based on our needs.
 * */
export const updateItemsUsedDetail = (data) => {
  const {
    questions,
    myQuiz,
    allStimulusQuestions,
    stimulusQuestionsInQuiz,
    quizId,
    quizTitle,
    checkAll,
    remove,
  } = data;
  const items = data?.items || [];
  const updatedItems = [...items];
  const updatedAllStimulusQuestions = { ...allStimulusQuestions };
  const updatedQuestions = Object.assign({}, questions);

  items.forEach((item, itemIndex) => {
    const isMyQuizItem = myQuiz.includes(item.item_id);
    let itemStimulus = null;
    let isMyQuizStimulusItem = false;

    if (!isMyQuizItem) {
      itemStimulus = getQuestionStimulus(
        { id: item.item_id },
        allStimulusQuestions,
      );
      isMyQuizStimulusItem =
        itemStimulus.id &&
        myQuiz.includes(itemStimulus.id) &&
        stimulusQuestionsInQuiz[itemStimulus.id].includes(item.item_id);

      if (!isMyQuizStimulusItem) {
        if (checkAll) {
          updatedItems[itemIndex] = removeUsedDetail(
            updatedItems[itemIndex],
            quizId,
          );

          if (itemStimulus && itemStimulus.id) {
            updatedAllStimulusQuestions[itemStimulus.id] =
              updatedAllStimulusQuestions[itemStimulus.id].map((question) =>
                question.item_id === item.item_id
                  ? removeUsedDetail(question, quizId)
                  : question,
              );
          }
        }

        return null;
      }
    }

    if (remove) {
      updatedItems[itemIndex] = removeUsedDetail(
        updatedItems[itemIndex],
        quizId,
      );

      if (isMyQuizStimulusItem && itemStimulus) {
        updatedAllStimulusQuestions[itemStimulus.id] =
          updatedAllStimulusQuestions[itemStimulus.id].map((question) =>
            question.item_id === item.item_id
              ? removeUsedDetail(question, quizId)
              : question,
          );
      }
    } else {
      const newUsedDetail = {
        assessment_title: quizTitle,
        classes: [],
      };

      const used_detail = updatedItems[itemIndex].used_detail;
      if (used_detail) {
        //merge existing with incoming details
        used_detail[quizId] = {
          ...newUsedDetail,
          ...(used_detail[quizId] || {}),
        };
      } else {
        updatedItems[itemIndex].used_detail = { [quizId]: newUsedDetail };
      }
      updatedItems[itemIndex].used_before = true;

      // Push same changes into `questions` state too
      // The check is necessary because of stimulus items
      if (updatedQuestions[item.item_id]) {
        // Only details about question usage
        updatedQuestions[item.item_id].used_detail = cloneDeep(
          updatedItems[itemIndex].used_detail,
        );
        updatedQuestions[item.item_id].used_before = true;
      }

      if (isMyQuizStimulusItem && itemStimulus) {
        updatedAllStimulusQuestions[itemStimulus.id] =
          updatedAllStimulusQuestions[itemStimulus.id].map((question) =>
            question.item_id === item.item_id
              ? {
                  ...question,
                  used_before: true,
                  used_detail: {
                    ...question.used_detail,
                    [quizId]: newUsedDetail,
                  },
                }
              : question,
          );
      }
    }
  });

  return {
    items: updatedItems,
    questions: updatedQuestions,
    allStimulusQuestions: updatedAllStimulusQuestions,
  };
};

/**
 * A simple and expressive ternary function
 * @param {Boolean} condition - The condition to be checked
 * @param {Any} ifTrue - Value when the condition is true
 * @param {Any} ifFalse - Value when the condition is false
 * @returns {Any} - One of of ifTrue or ifFalse values
 * */
export const branch = (condition, ifTrue, ifFalse) =>
  Boolean(condition) ? ifTrue : ifFalse;
